import { useState } from "react";
import { useDeleteMessageTemplate } from "@/api/message-templates";
import { Button } from "@/components/base/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { success as toastSuccess } from "@/providers/ToastProvider";

interface DeleteTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  templateName: string;
}

type InUseState = { policy: boolean; sequence: boolean };

export function DeleteTemplateDialog({ open, onOpenChange, templateId, templateName }: DeleteTemplateDialogProps) {
  const { mutateAsync: deleteTemplate, isPending } = useDeleteMessageTemplate();
  const [inUse, setInUse] = useState<InUseState | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleConfirm = async () => {
    setErrorMessage(null);
    try {
      const result = await deleteTemplate({ id: templateId, force: inUse !== null });
      if (result.inUse) {
        setInUse(result.inUse);
        return;
      }
      toastSuccess("Template deleted");
      onOpenChange(false);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to delete template");
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setInUse(null);
      setErrorMessage(null);
    }
    onOpenChange(nextOpen);
  };

  const usageLabel = inUse
    ? inUse.policy && inUse.sequence
      ? "a risk policy and a sequence"
      : inUse.policy
        ? "a risk policy"
        : "a sequence"
    : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete Template</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{templateName}</strong>?
          </DialogDescription>
        </DialogHeader>

        {inUse && (
          <div className="text-sm rounded-md bg-utility-warning-50 border border-utility-warning-200 px-3 py-2.5 flex flex-col gap-1.5 text-warning-700">
            <p className="font-medium">This template is currently used by {usageLabel}.</p>
            <p>
              If you delete it, {usageLabel === "a risk policy and a sequence" ? "they" : "it"} will continue using the
              template's content as a custom message — but this template will no longer be available to select in other
              places.
            </p>
            <p>
              You can edit the{" "}
              {inUse.policy && inUse.sequence ? "policy or sequence" : inUse.policy ? "policy" : "sequence"} to use a
              different template before deleting.
            </p>
          </div>
        )}

        {errorMessage && (
          <div className="text-sm text-error-600 rounded-md bg-utility-error-50 border border-utility-error-200 px-3 py-2.5">
            <p>{errorMessage}</p>
          </div>
        )}

        <DialogFooter>
          <Button color="secondary" size="sm" onClick={() => handleOpenChange(false)} isDisabled={isPending}>
            Cancel
          </Button>
          <Button color="primary-destructive" size="sm" onClick={handleConfirm} isLoading={isPending}>
            {inUse ? "Delete Template" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
